import Image from "next/image";

import { FC, useEffect, useState } from "react";

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import Box, { BoxProps } from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import Heading from "sections/nfcTagsPage/Heading";
import DiscoveryCard from "sections/nfcTagsPage/DiscoveryCard";

import { resetContainerPaddings } from "utils/styles";

import styles from "./styles";

gsap.registerPlugin(ScrollTrigger);
ScrollTrigger.config({
  autoRefreshEvents: "load , visibilitychange , DOMContentLoaded",
  ignoreMobileResize: true,
});

interface DiscoverySectionProps {
  boxProps?: BoxProps;
  isReadyToStart: boolean;
  setSectionLoaded?: () => void;
}

const DiscoverySection: FC<DiscoverySectionProps> = ({
  boxProps,
  isReadyToStart,
  setSectionLoaded,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const [isSmart, setSmart] = useState(true);

  const onHandleSmart = () => {
    setSmart(true);
  };

  const onHandleStandard = () => {
    setSmart(false);
  };

  useEffect(() => {
    if (!isReadyToStart) return;

    gsap.from("#ds-card", {
      opacity: 0,
      yPercent: 50,
      ease: "circ",
      scrollTrigger: {
        trigger: "#ds-wrapper",
        start: "center bottom",
        end: "+=1000",
        scrub: 2,
        toggleActions: "restart none none reverse",
      },
    });
    gsap.fromTo(
      "#ds-sneakers",
      {
        opacity: 0,
        yPercent: 75,
        xPercent: -50,
      },
      {
        opacity: 1,
        yPercent: -50,
        xPercent: -50,

        ease: "circ",
        scrollTrigger: {
          trigger: "#ds-wrapper",
          start: "center bottom",
          end: "+=1000",
          scrub: 2,
          toggleActions: "restart none none reverse",
        },
      },
    );
    const tl = gsap.timeline({
      duration: 100,
      scrollTrigger: {
        trigger: "#ds-wrapper",
        start: isDesktop ? "center center" : "center 45%",
        end: "+=5000",
        scrub: 0.5,
        anticipatePin: 1,
        pin: "#ds-wrapper",
        toggleActions: "restart none none reverse",
      },
      onStart: onHandleStandard,
    });
    // Show texts
    tl.from("#ds-left", { opacity: 0, xPercent: -200, duration: 200 });
    tl.from(
      "#ds-right",
      {
        opacity: 0,
        xPercent: 200,
        duration: 200,
      },
      "<",
    );

    // Hide texts
    tl.to("#ds-left", {
      opacity: 0,
      duration: 200,
      delay: 100,
      onComplete: onHandleSmart,
    });
    tl.to("#ds-right", { opacity: 0, duration: 200 }, "<");

    // Discover
    tl.fromTo(
      "#ds-border",
      {
        y: 430,
        xPercent: -50,
        opacity: -1,
      },
      {
        y: 0,
        opacity: 1,
        xPercent: -50,
        duration: 350,
        delay: 100,
      },
    );
    tl.from("#ds-cube", { y: 360, opacity: -1, duration: 320 }, "<");
    tl.from("#ds-cart", { y: 390, opacity: -1, duration: 380 }, "<");
    tl.from("#ds-nfc", { y: 420, opacity: -1, duration: 300 }, "<");
    tl.from("#ds-nft", { y: 470, opacity: -1, duration: 420 }, "<");

    // // Show new texts
    tl.to("#ds-left", {
      opacity: 1,
      duration: 200,
      delay: 100,
      onReverseComplete: onHandleStandard,
    });
    tl.to("#ds-right", { opacity: 1, duration: 200 }, "<");

    // End
    tl.from("#ds-nft", { duration: 100 });
    // Banner
    gsap.to("#ds-banner", {
      x: isDesktop ? -970 : -1340,
      ease: "linear",
      scrollTrigger: {
        trigger: "#ds-banner",
        start: "bottom bottom",
        end: "top top",
        scrub: true,
        toggleActions: "restart none none reverse",
      },
    });

    setSectionLoaded && setSectionLoaded();
  }, [isDesktop, isReadyToStart, setSectionLoaded]);

  return (
    <Box component="section" {...boxProps}>
      <Heading
        heading="How it works"
        subheading="Take your products to the next level. Use our technology to expand the
          user experience, create the next generation of physical things, and
          add new digital layers with enhanced product data."
      />

      <Box id="ds-wrapper" sx={styles.design}>
        <Box id="ds-left" sx={styles.leftColumn}>
          <Box>
            <Typography variant="h2" component="h2" sx={styles.leftHeading}>
              {isSmart ? "Smart product" : "Standard product"}
            </Typography>
            <Typography
              sx={(theme) =>
                styles.leftSubheading(theme, { isVisible: isSmart })
              }
              variant="h3"
            >
              phygital
            </Typography>
          </Box>
          <Box sx={{ transform: isSmart ? "" : "rotate(90deg)" }}>
            <Image
              src={`${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/pages/nfcTagsPage/discovery/arrow.svg`}
              alt=""
              width={isDesktop ? 62 : 36}
              height={isDesktop ? 62 : 36}
              style={{ objectFit: "scale-down" }}
            />
          </Box>
        </Box>

        <Box id="ds-card" sx={styles.cardWrapper}>
          <DiscoveryCard />
        </Box>

        <Box id="ds-right">
          <Box sx={{ display: "grid" }}>
            <Typography
              variant="h3"
              sx={[
                styles.rightContent,
                {
                  opacity: isSmart ? 1 : 0,
                  pointerEvents: isSmart ? "all" : "none",
                },
              ]}
            >
              Next generation product. Visually, it is almost the same,
              technologically it's an entirely new one - phygital. We create it
              by digitizing it with a blockchain-based technology that adds a
              new digital layer of data. Customers gain a new augmented
              experience allowing them to interact with your product on a new
              level.
            </Typography>
            <Typography
              variant="h3"
              sx={[
                styles.rightContent,
                {
                  opacity: isSmart ? 0 : 1,
                  pointerEvents: isSmart ? "none" : "all",
                },
              ]}
            >
              This is an ordinary product made with new, elegant, and
              high-quality materials. It is ordinary because it has not been
              digitalized since that simply was not possible before.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={[
          styles.banner,
          {
            mx: resetContainerPaddings,
            px: `calc(${resetContainerPaddings} * -1)`,
          },
        ]}
      >
        <Box sx={styles.bannerDivider} />
        <Typography
          variant="h3"
          id="ds-banner"
          sx={[styles.bannerTextWrapper, { fontSize: { xs: 80, lg: 100 } }]}
        >
          <Box sx={styles.bannerText}>Blockchain</Box>
          <Box sx={[styles.bannerText, styles.outlineText]}>Nft</Box>
          <Box sx={styles.bannerText}>E-commerce</Box>
          <Box sx={[styles.bannerText, styles.outlineText]}>Nfc</Box>
        </Typography>
        <Box sx={styles.bannerDivider} />
      </Box>
    </Box>
  );
};

export default DiscoverySection;
